import { io, Socket } from "socket.io-client";
import { API_CONFIG } from "@shared/constants/api";
import { SocketEvents } from "./events";

// Default configuration for the socket connection
const DEFAULT_OPTIONS = {
  transports: ["websocket"],
  reconnection: true,
  reconnectionAttempts: 10,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  timeout: 20000,
};

class SocketClient {
  private socket: Socket | null = null;
  private token: string | null = null;
  private isConnecting: boolean = false;
  private listeners: Map<string, Set<(...args: any[]) => void>> = new Map();

  private static instance: SocketClient;

  private constructor() { }

  // Singleton pattern
  public static getInstance(): SocketClient {
    if (!SocketClient.instance) {
      SocketClient.instance = new SocketClient();
    }
    return SocketClient.instance;
  }

  /**
   * Connect to the socket server
   * @param token Authentication token
   */
  public connect(token: string): Socket {
    if (this.socket?.connected) {
      // If already connected with the same token, do nothing
      if (this.token === token) {
        return this.socket;
      }
      // If token changed, disconnect and reconnect
      this.disconnect();
    }

    this.token = token;
    this.isConnecting = true;

    this.socket = io(API_CONFIG.socketUrl, {
      ...DEFAULT_OPTIONS,
      auth: {
        token: `Bearer ${token}`,
      },
      query: {
      }
    });

    this.setupBaseListeners();
    this.applyBufferedListeners();

    return this.socket;
  }

  /**
   * Disconnect from the socket server
   */
  public disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnecting = false;
      this.token = null;
    }
  }

  /**
   * Get the current socket instance
   */
  public getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Check if socket is connected
   */
  public isConnected(): boolean {
    return !!this.socket?.connected;
  }

  /**
   * Internal listeners for connection status logging
   */
  private setupBaseListeners() {
    if (!this.socket) return;

    this.socket.on(SocketEvents.CONNECT, () => {
      console.log(`[Socket] Connected: ${this.socket?.id}`);
      this.isConnecting = false;
    });

    this.socket.on(SocketEvents.DISCONNECT, (reason) => {
      console.log(`[Socket] Disconnected: ${reason}`);
      this.isConnecting = false;
    });

    this.socket.on(SocketEvents.RECONNECT, (attempt) => {
      console.log(`[Socket] Reconnected after attempt: ${attempt}`);
    });

    this.socket.on("connect_error", (err) => {
      console.error(`[Socket] Connection error:`, err);
      this.isConnecting = false;
    });
  }

  /**
   * Re-apply all registered listeners to the new socket instance
   */
  private applyBufferedListeners() {
    if (!this.socket) return;

    this.listeners.forEach((callbacks, event) => {
      callbacks.forEach(callback => {
        this.socket?.on(event, callback);
      });
    });
  }

  /**
   * Emit an event
   */
  public emit(event: string, data?: any): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn(`[Socket] Cannot emit '${event}': Socket not connected`);
    }
  }

  /**
   * Register an event listener
   */
  public on(event: string, callback: (...args: any[]) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  /**
   * Remove an event listener
   */
  public off(event: string, callback?: (...args: any[]) => void): void {
    if (callback) {
      const callbacks = this.listeners.get(event);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.listeners.delete(event);
        }
      }
    } else {
      this.listeners.delete(event);
    }

    if (this.socket) {
      this.socket.off(event, callback);
    }
  }
}

export const socketClient = SocketClient.getInstance();
export default socketClient;
